"use client";

import React from "react";

import IdDisplay from "@/components/ui/id-display";

import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";

import { cx } from "@/lib/tailwind-utils";

import type { PatientIdentifierDisplay } from "@/types/tenant";

interface PatientIdentifierSource {
  id: string;
  mrn?: string | null;
}

/** The clinic's choice of patient identifier (Settings > Medical Record Numbers). */
export function usePatientIdentifierDisplay(): PatientIdentifierDisplay {
  const { tenant } = useTenant();
  return tenant?.patientIdentifierDisplay ?? "DECODA_ID";
}

/**
 * The patient ID as plain text, for exports, printouts and copy. Falls back to the
 * Decoda ID while a patient has no MRN, so it is never blank.
 */
export function patientIdentifierText(
  display: PatientIdentifierDisplay,
  patient: PatientIdentifierSource,
): string {
  if (!patient.mrn || display === "DECODA_ID") return patient.id;
  if (display === "MRN") return patient.mrn;
  return `${patient.mrn} (${patient.id})`;
}

interface PatientIdentifierProps {
  patient: PatientIdentifierSource;
  className?: string;
  /** Truncate the Decoda ID. The MRN is short and is never truncated. */
  truncate?: boolean;
  copyDisabled?: boolean;
  /** When true, show even when the user's Show patient IDs setting is off. */
  alwaysShow?: boolean;
}

/**
 * Use this, not `IdDisplay`, wherever a patient's ID is shown. It shows the Decoda ID,
 * the MRN or both depending on the clinic's setting, and respects the user's Show
 * patient IDs setting (separate from Show IDs, which covers every other ID).
 */
export function PatientIdentifier({
  patient,
  className,
  truncate = true,
  copyDisabled,
  alwaysShow,
}: PatientIdentifierProps) {
  const display = usePatientIdentifierDisplay();
  const { currentUser } = useUser();
  // Falls back to Show IDs while the API predates showPatientIds (console deployed first).
  const showPatientIds = currentUser?.showPatientIds ?? currentUser?.showIds;
  if (!alwaysShow && !showPatientIds) return null;

  // Gated on Show patient IDs above, so the inner IdDisplays skip their Show IDs check.
  const showMrn = display !== "DECODA_ID" && !!patient.mrn;
  const showDecodaId = display !== "MRN" || !patient.mrn;

  if (showMrn && showDecodaId) {
    return (
      <div className={cx("flex flex-wrap items-center gap-x-3", className)}>
        <IdDisplay
          id={patient.mrn!}
          label="MRN"
          truncate={false}
          copyDisabled={copyDisabled}
          alwaysShow
        />
        <IdDisplay
          id={patient.id}
          label="Decoda ID"
          truncate={truncate}
          copyDisabled={copyDisabled}
          alwaysShow
        />
      </div>
    );
  }

  return (
    <IdDisplay
      id={showMrn ? patient.mrn! : patient.id}
      label={showMrn ? "MRN" : "Decoda ID"}
      truncate={showMrn ? false : truncate}
      copyDisabled={copyDisabled}
      alwaysShow
      className={className}
    />
  );
}

export default PatientIdentifier;
